import styles from './productcarousel.module.scss';
import ProductCarousel from './ProductCarousel';
import {
  OBSObjI,
  ProductCarouselData,
  ProductCarouselWrapperProps,
} from '@msc-b2c/interfaces/types';
import { ProductCarouselContext } from './ProductCarousel.hooks';
import { useSelector } from 'react-redux';
import { MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { getActiveSession, RootState, useGetBookingQuery } from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  cabinOfTheLoggedUser,
  getCabinIdOfTheLoggedUser,
  getDepartureDate,
  isXDaysBeforeDeparture,
} from '../utils/myAreaUtils';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { useMemo } from 'react';
import { filterPackagesOnCartOrAlreadyPurchased } from '../utils/myAreaCartUtils';
import { filterExcursionsForAllPortsView } from '../utils/excursionFilterUtils';

export function ProductCarouselWrapper({
  preTitle,
  title,
  subtitle,
  leftArrowAltText,
  rightArrowAltText,
  macroCategoryOrder,
  buttonLabel,
  perAdultLabel,
  perCabinLabel,
  perPackageLabel,
  perChildLabel,
  dataComponentName,
  serviceTypeOrder,
  carouselMaxItems,
  enableLowestPriceLogic,
  visibleInFullLogin,
  visibleInLightLogin,
  buttonAriaLabel,
}: Readonly<ProductCarouselWrapperProps>) {
  const myMscExcursions: MyMscExcursion[] =
    useSelector((state: RootState) => state.myCruise.excursions?.excursions) || [];
  const myMscObs: OBSObjI[] =
    useSelector((state: RootState) => state.myCruise.obs?.onboardServices) || [];
  const activeCruiseId = useSelector((state: RootState) => state.myCruise.activeCruiseId ?? null);

  const { items: cart } = useSelector((state: RootState) => state.myAreaCart);

  const activeBookingNumber = useSelector(
    (state: RootState) => state.myCruise.activeBookingNumber ?? null
  );

  const { loginMode, bookingNumber = '' } = getActiveSession();

  const hasBooking = Boolean(activeBookingNumber || bookingNumber); // check if there is a linked booking

  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const data = useMemo<ProductCarouselData>(() => {
    const excursionsAfterCart = filterPackagesOnCartOrAlreadyPurchased(
      myMscExcursions,
      booking,
      cart
    ) as MyMscExcursion[];
    const cabinLoggedUser = booking
      ? cabinOfTheLoggedUser(booking.cabins, getCabinIdOfTheLoggedUser(booking))
      : undefined;

    return {
      excursionsData: cabinLoggedUser
        ? filterExcursionsForAllPortsView(excursionsAfterCart, cabinLoggedUser)
        : excursionsAfterCart.filter((exc) => Number(exc.inventory?.numberAvailable) > 0),
      obsData: filterPackagesOnCartOrAlreadyPurchased(myMscObs, booking, cart) as OBSObjI[],
    };
  }, [myMscExcursions, myMscObs, cart, booking]);

  const props = {
    preTitle,
    title,
    subtitle,
    leftArrowAltText,
    rightArrowAltText,
    macroCategoryOrder,
    buttonLabel,
    perAdultLabel,
    perCabinLabel,
    perPackageLabel,
    perChildLabel,
    dataComponentName,
    serviceTypeOrder,
    carouselMaxItems,
    enableLowestPriceLogic,
    visibleInFullLogin,
    visibleInLightLogin,
    buttonAriaLabel,
  };

  //Hide component based on booking status, departure proximity, login mode, or empty category list
  // for backwards compatibility from AEM hideInFullLogin is false
  // hideInLightLogin is true so we keep current visibility rule (in case of already placed carousel)
  if (
    (booking &&
      (activeCruiseId || getActiveSession().cruiseId) &&
      isXDaysBeforeDeparture(getDepartureDate(booking))) || // Booking is too close to departure
    (loginMode === LoginMode.LIGHT && !visibleInLightLogin) ||
    (loginMode === LoginMode.FULL && !visibleInFullLogin) ||
    macroCategoryOrder.length === 0 || // No macro categories available
    (booking?.status.code === BookingStatusEnum.OPTIONAL && booking) || // Booking is optional
    !hasBooking // hide if no bookings linked (cart scenario)
  ) {
    return null;
  }

  return (
    data && (
      <ProductCarouselContext.Provider
        value={{
          productCarouselAEMProps: props,
          productCarouselData: data,
        }}
      >
        <div className={styles['cmp-productcarousel-wrapper']}>
          <ProductCarousel
            dataComponentName={dataComponentName}
            preTitle={preTitle}
            title={title}
            subtitle={subtitle}
          />
        </div>
      </ProductCarouselContext.Provider>
    )
  );
}

ProductCarouselWrapper.aemResourceType = 'b2c-website-foundation/components/mymsc/productcarousel';

export default ProductCarouselWrapper;
